import React, { useState, useEffect, useRef } from "react";
import Loader from "../../components/Loader";
import User_Navbar from "./User_Navbar";
import Calling from "../../api/Calling";
import { toast } from "react-toastify";
import img1 from "../../Image/Profile.png";
import formatDateTime from "../../components/formatDateTime";
import { IoMdArrowDropdown } from "react-icons/io";
import { FaFileExcel, FaFilePdf } from "react-icons/fa";

import {
  FaArrowLeft,
  FaArrowRight,
  FaEye,
  FaFileDownload,
  FaSortAmountDownAlt,
  FaSortAmountUp,
  FaTrash,
} from "react-icons/fa";
import generateCaptcha from "../../components/captcha";
import { useNavigate } from "react-router-dom";
import MainButton from "../../components/ul/MainButton";
import ActionButton from "../../components/ul/ActionButton";
import { IoMdSearch } from "react-icons/io";
import DeleteButton from "../../components/ul/DeleteButton";
import { MdDeleteSweep } from "react-icons/md";
import { LuDot } from "react-icons/lu";
import NotFound from "../../components/ul/NotFound";
import getISTDate from "../../components/getISTDate";
import toIST from "../../components/toIST";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PiDownloadSimple } from "react-icons/pi"; // download icon

const User = () => {
  const [loader, setLoader] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [sortOrder, setSortOrder] = useState(-1);
  const [selectAll, setSelectAll] = useState(false);
  const [startDate, setStartDate] = useState(getISTDate());
  const [endDate, setEndDate] = useState(getISTDate());
  const [totalUser, setTotalUser] = useState(0);
  const [blockUser, setBlockUser] = useState(false);
  const [selectState, setSelectState] = useState("today");
  const limit = 200;
  const navigate = useNavigate();

  // ✅ Fetch only created user data
  const fetchData = async () => {
    setLoader(true);
    try {
      const response = await Calling.GetAllUserData(
        currentPage,
        startDate,
        endDate,
        limit,
        search,
        sortOrder,
        blockUser,
        "",
        "created" // always created filter
      );
      if (response?.status) {
        setData(response?.data);
        setTotalPages(response?.totalPage);
        setTotalUser(response?.totalRecord);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [currentPage, sortOrder, startDate, endDate, blockUser]);

  const handleSelectAll = () => {
    if (!selectAll) {
      const allUserIds = data.map((user) => user._id);
      setSelectedUsers(allUserIds);
    } else {
      setSelectedUsers([]);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectUser = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]
    );
  };

  const handleDateRangeFilter = (range) => {
    const today = new Date();
    let start, end;

    switch (range) {
      case "today":
        start = end = toIST(today);
        break;

      case "yesterday":
        const y = new Date(today);
        y.setDate(today.getDate() - 1);
        start = end = toIST(y);
        break;

      case "thisWeek":
        const first = new Date(today);
        first.setDate(today.getDate() - today.getDay());
        const last = new Date(first);
        last.setDate(first.getDate() + 6);
        start = toIST(first);
        end = toIST(last);
        break;

      case "thisMonth":
        const sm = new Date(today.getFullYear(), today.getMonth(), 1);
        const em = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        start = toIST(sm);
        end = toIST(em);
        break;

      case "last6Months":
        const s6 = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        const e6 = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        start = toIST(s6);
        end = toIST(e6);
        break;

      case "thisYear":
        const sy = new Date(today.getFullYear(), 0, 1);
        const ey = new Date(today.getFullYear(), 11, 31);
        start = toIST(sy);
        end = toIST(ey);
        break;

      default:
        start = end = "";
    }

    setSelectState(range);
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
    fetchData();
  };

  const handleDeleteSelected = async (id) => {
    const check = generateCaptcha(
      "You are about to delete a user. To confirm, please solve this math problem."
    );
    if (!check) return toast.error("Captcha answer is incorrect");
    try {
      setLoader(true);
      const body = { id: id ? [id] : selectedUsers };
      const response = await Calling.DeleteUser(body);
      if (response?.status) {
        toast.success(response?.message);
        setSelectedUsers([]);
        setSelectAll(false);
        fetchData();
      } else toast.error(response?.message);
    } catch (e) {
      console.error("Error deleting users:", e);
    } finally {
      setLoader(false);
    }
  };

  const handleEdit = (id) => navigate(`/manage/user/all/info/${id}`);

  const handleBlockUser = async (id, status) => {
    setLoader(true);
    try {
      const res = await Calling.BlockUser(id, status);
      res?.status ? toast.success(res?.message) : toast.error(res?.message);
      fetchData();
    } catch (err) {
      console.error("Error blocking user:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleReportSort = () => {
    setSelectState("sort");
    setSortOrder(sortOrder === 1 ? -1 : 1);
  };

  const handleBlockFilter = () => {
    setBlockUser(!blockUser);
    setSelectState("block");
  };

  const handleFormDate = (e) => {
    if (e.target.name === "startDate") {
      setStartDate(e.target.value);
    } else {
      setEndDate(e.target.value);
    }
    setSelectState("range");
  };

  const handleSearchUser = (e) => {
    setCurrentPage(1);
    setSearch(e.target.value);
    setSelectState("search");
  };

  useEffect(() => {
    if (search.trim() !== "") {
      const delay = setTimeout(() => fetchData(), 500);
      return () => clearTimeout(delay);
    }
  }, [search]);

  const handleDownloadExcel = async () => {
    const resp = await Calling.GetAllUserExal(startDate, endDate);

    if (!resp?.status) {
      return toast.error(resp.message || "No data found");
    }

    const users = resp.data;

    if (!users?.length) {
      return toast.error("No users available for this date range");
    }

    // Prepare Excel rows
    const excelRows = users.map((u, i) => ({
      No: i + 1,
      Name: u.u_name || "",
      Email: u.u_email || "",
    }));

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelRows);
    XLSX.utils.book_append_sheet(wb, ws, "Users");

    // Write file
    XLSX.writeFile(wb, `Users_${new Date().toISOString()}.xlsx`);
  };

  const handleDownloadPDF = async () => {
    try {
      // 1️⃣ CALL YOUR API
      const resp = await Calling?.GetAllUserExal(startDate, endDate);

      if (!resp?.status) {
        return toast.error(resp?.message || "No data found");
      }

      const users = resp?.data || [];

      if (users.length === 0) {
        return toast.error("No users found in this date range");
      }

      // 2️⃣ PREPARE PDF ROWS
      const pdfRows = users.map((u, i) => [
        i + 1,
        u.u_name || "",
        u.u_email || "",
      ]);

      // 3️⃣ CREATE PDF
      const doc = new jsPDF();

      doc.setFontSize(16);
      doc.text("User Report", 14, 15);

      autoTable(doc, {
        head: [["No", "Name", "Email"]],
        body: pdfRows,
        startY: 25,
      });

      // 4️⃣ DOWNLOAD PDF
      doc.save(`Users_${new Date().toISOString()}.pdf`);
    } catch (error) {
      console.log("PDF download error:", error);
      toast.error("Something went wrong");
    }
  };

  return (
    <>
      <div className="w-full md:ml-[4.4rem] px-3 sm:px-4 lg:px-6 mt-16">
        <div className="bg-white rounded-xl shadow-sm mt-4 overflow-hidden">
          <h1 className="text-xl sm:text-2xl font-bold px-4 py-3 text-gray-800">
            User Management
          </h1>

          <hr className="border-gray-200" />

          {/* Date Range Filters */}
          <div className="flex flex-col lg:flex-row lg:justify-between gap-3 p-3 sm:p-4">
            {/* Mobile Dropdown */}
            <div className="block sm:hidden">
              <select
                className="w-full border bg-white px-3 py-2 rounded-lg text-sm shadow-sm cursor-pointer"
                onChange={(e) => handleDateRangeFilter(e.target.value)}
                value={selectState}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="thisWeek">This Week</option>
                <option value="thisMonth">This Month</option>
                <option value="last6Months">Last 6 Months</option>
                <option value="thisYear">This Year</option>
                <option value="range">Custom Range</option>
              </select>
            </div>

            {/* Desktop Filter Buttons */}
            <div className="hidden sm:flex flex-wrap gap-2">
              <MainButton
                label="Today"
                selectState={selectState === "today"}
                onClick={() => handleDateRangeFilter("today")}
              />
              <MainButton
                label="Yesterday"
                selectState={selectState === "yesterday"}
                onClick={() => handleDateRangeFilter("yesterday")}
              />
              <MainButton
                label="This Week"
                selectState={selectState === "thisWeek"}
                onClick={() => handleDateRangeFilter("thisWeek")}
              />
              <MainButton
                label="This Month"
                selectState={selectState === "thisMonth"}
                onClick={() => handleDateRangeFilter("thisMonth")}
              />
              <MainButton
                label="Last 6 Months"
                selectState={selectState === "last6Months"}
                onClick={() => handleDateRangeFilter("last6Months")}
              />
              <MainButton
                label="This Year"
                selectState={selectState === "thisYear"}
                onClick={() => handleDateRangeFilter("thisYear")}
              />
            </div>

            {/* Custom Range Inputs */}
            <div className="hidden md:flex gap-2">
              <input
                type="date"
                value={startDate}
                name="startDate"
                onChange={handleFormDate}
                className={`border px-3 py-2 rounded-lg cursor-pointer ${
                  selectState === "range"
                    ? "border-blue-600 text-blue-600"
                    : "border-gray-300 text-gray-700"
                }`}
              />

              <input
                type="date"
                value={endDate}
                name="endDate"
                onChange={handleFormDate}
                className={`border px-3 py-2 rounded-lg cursor-pointer ${
                  selectState === "range"
                    ? "border-blue-600 text-blue-600"
                    : "border-gray-300 text-gray-700"
                }`}
              />
            </div>
          </div>

          <hr className="border-gray-100 mx-2 sm:mx-4" />

          {/* Actions + Search */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3  p-3 sm:p-4">
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <MainButton
                onClick={handleBlockFilter}
                selectState={selectState === "block"}
                label={blockUser ? "Unblock" : "Block"}
              />

              <MainButton
                onClick={handleReportSort}
                selectState={selectState === "sort"}
                label={
                  <div className="flex gap-2 items-center">
                    {sortOrder === -1 ? (
                      <FaSortAmountDownAlt />
                    ) : (
                      <FaSortAmountUp />
                    )}
                    <span>Sort</span>
                  </div>
                }
              />
              <DownloadDropdown
                onDownloadExcel={handleDownloadExcel}
                onDownloadPDF={handleDownloadPDF}
              />
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-[250px] md:w-[300px]">
              <IoMdSearch
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                size={18}
              />
              <input
                type="search"
                placeholder="Search"
                value={search}
                onChange={handleSearchUser}
                className="w-full p-2 pl-10 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-600 outline-none"
              />
            </div>
          </div>

          {/* User Count */}
          <div className="p-4 bg-main-th mb-4 rounded-xl mx-2 sm:mx-4 ">
            <h2 className="text-base sm:text-lg text-gray-600">
              Total User Created
            </h2>
            <p className="text-xl font-semibold text-gray-800">
              {totalUser < 10 ? "0" + totalUser : totalUser}
            </p>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-xl shadow-sm border border-gray-200">
            <table className="min-w-full border-separate border-spacing-y-2 text-left">
              <thead className="sticky top-0 bg-main-th text-gray-800 text-xs uppercase">
                <tr>
                  <th className="pl-4 py-3 w-10">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="accent-main"
                      />
                      {selectedUsers.length > 0 && (
                        <DeleteButton
                          onClick={() => handleDeleteSelected(null)}
                          label={<MdDeleteSweep size={18} />}
                        />
                      )}
                    </div>
                  </th>

                  <th className="px-3 py-3 w-10">#</th>
                  <th className="px-3 py-3 min-w-20">Profile</th>
                  <th className="px-3 py-3 min-w-40">Name</th>
                  <th className="px-3 py-3 min-w-48">Email</th>
                  <th className="px-3 py-3 min-w-20">Coin</th>
                  <th className="px-3 py-3 min-w-24">Status</th>
                  <th className="px-3 py-3 min-w-32">Date</th>
                  <th className="px-3 py-3 min-w-28 text-center">Actions</th>
                </tr>
              </thead>

              <tbody className="transition-all duration-300">
                {loader ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr
                      key={i}
                      className="animate-pulse bg-white border-b border-gray-100"
                    >
                      <td className="pl-4 py-4">
                        <div className="w-4 h-4 bg-gray-200 rounded" />
                      </td>
                      <td className="px-3 py-4">
                        <div className="w-6 h-3 bg-gray-200 rounded" />
                      </td>
                      <td className="px-3 py-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-full mx-auto" />
                      </td>
                      <td className="px-3 py-4">
                        <div className="w-24 h-4 bg-gray-200 rounded mb-2" />
                        <div className="w-12 h-3 bg-gray-100 rounded" />
                      </td>
                      <td className="px-3 py-4">
                        <div className="w-28 h-3 bg-gray-200 rounded" />
                      </td>
                      <td className="px-3 py-4">
                        <div className="w-10 h-3 bg-gray-200 rounded" />
                      </td>
                      <td className="px-3 py-4">
                        <div className="w-16 h-5 bg-gray-200 rounded-full" />
                      </td>
                      <td className="px-3 py-4">
                        <div className="w-20 h-3 bg-gray-200 rounded" />
                      </td>
                      <td className="px-3 py-4">
                        <div className="flex gap-2 justify-center">
                          <div className="w-6 h-6 bg-gray-200 rounded-md" />
                          <div className="w-6 h-6 bg-gray-200 rounded-md" />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : data.length > 0 ? (
                  data.map((user, index) => (
                    <tr
                      key={user._id}
                      className="bg-white hover:bg-main-th transition-colors border-b border-gray-100"
                    >
                      <td className="pl-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user._id)}
                          onChange={() => handleSelectUser(user._id)}
                          className="accent-main"
                        />
                      </td>

                      <td className="px-3 py-3">
                        {index + 1 + (currentPage - 1) * limit}
                      </td>

                      <td
                        className="px-3 py-3 cursor-pointer"
                        onClick={() => handleEdit(user._id)}
                      >
                        <img
                          src={user?.u_profile || img1}
                          alt=""
                          className="w-10 h-10 rounded-full border border-gray-200 shadow-sm"
                        />
                      </td>

                      <td className="px-3 py-3 text-sm font-semibold break-words cursor-pointer">
                        {user.u_name}
                      </td>
                      <td className="px-3 py-3 text-sm break-words cursor-pointer">
                        {user.u_email}
                      </td>

                      <td className="px-3 py-3 font-semibold cursor-pointer">
                        🪙 {user.u_coin || 0}
                      </td>

                      <td className="px-3 py-3">
                        {user.u_status ? (
                          <span className="inline-flex items-center px-3 py-1 text-xs rounded-full bg-green-50 text-green-700 gap-1">
                            <LuDot size={14} className="text-green-400" />{" "}
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 text-xs rounded-full bg-red-50 text-red-700 gap-1">
                            <LuDot size={14} className="text-red-400" /> Blocked
                          </span>
                        )}
                      </td>

                      <td className="px-3 py-3 text-xs text-gray-500">
                        {formatDateTime(user.createdAt)}
                      </td>

                      <td className="px-3 py-3 text-center">
                        <ActionButtons
                          v={user}
                          handleDeleteSelected={handleDeleteSelected}
                          handleEdit={handleEdit}
                          handleBlockUser={handleBlockUser}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9}>
                      <NotFound />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5 px-4 py-6 border-t bg-gray-50 rounded-b-2xl">
            {/* Left Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-5 py-2.5 text-sm font-medium rounded-xl shadow-sm flex items-center gap-2 transition-all ${
                  currentPage === 1
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                <FaArrowLeft size={14} /> Previous
              </button>

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className={`px-5 py-2.5 text-sm font-medium rounded-xl shadow-sm flex items-center gap-2 transition-all ${
                  currentPage === totalPages
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                Next <FaArrowRight size={14} />
              </button>
            </div>

            {/* Page Counter */}
            <div className="flex items-center gap-2">
              <span className="px-4 py-2 bg-white border border-gray-300 rounded-xl shadow-sm font-semibold text-gray-700 min-w-[50px] text-center">
                {currentPage <= 9 ? `0${currentPage}` : currentPage}
              </span>
              <span className="text-gray-600 font-medium">/</span>
              <span className="px-4 py-2 bg-white border border-gray-300 rounded-xl shadow-sm font-semibold text-gray-700 min-w-[50px] text-center">
                {totalPages <= 9 ? `0${totalPages}` : totalPages}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default User;

const ActionButtons = ({
  v,
  handleDeleteSelected,
  handleEdit,
  handleBlockUser,
}) => {
  const [showActions, setShowActions] = useState(false);
  const handleButtonClick = () => {
    setShowActions(!showActions);
  };
  return (
    <div className="relative inline-block">
      <ActionButton onClick={handleButtonClick} />

      {showActions && (
        <div className="absolute left-0 mt-2 bg-main-white border border-main-border rounded shadow-lg z-[100]">
          <div className="flex items-center justify-left ms-4 w-full my-2">
            <label className="cursor-pointer mt-1">
              <input
                type="checkbox"
                checked={v?.u_status} // Reflect state in checkbox
                onChange={(e) => handleBlockUser(v?._id, !v?.u_status)} // Handle checkbox toggle
                className="sr-only peer"
                name="user_status"
              />
              <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-main-white after:border-main-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-600"></div>
            </label>
          </div>
          <button
            className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-100 w-full text-left"
            onClick={() => handleEdit(v?._id)}
          >
            <FaEye size={20} className="me-2" /> View
          </button>
          <button
            className="flex items-center px-4 py-2 text-red-600 hover:bg-red-100 w-full text-left"
            onClick={() => handleDeleteSelected(v?._id)}
          >
            <FaTrash className="mr-2" /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

const DownloadDropdown = ({ onDownloadExcel, onDownloadPDF }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownload = (type) => {
    if (type === "excel") onDownloadExcel();
    else if (type === "pdf") onDownloadPDF();
    setShowDropdown(false);
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-4 py-2.5 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 transition-colors shadow-sm font-medium text-sm"
      >
        <PiDownloadSimple size={18} />
        <span>Download</span>
        <IoMdArrowDropdown
          size={18}
          className={`transition-transform ${showDropdown ? "rotate-180" : ""}`}
        />
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <button
            onClick={() => handleDownload("excel")}
            className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-green-50 w-full text-left transition-colors"
          >
            <FaFileExcel size={18} className="text-green-600" />
            <span className="font-medium">Download Excel</span>
          </button>
          <div className="border-t border-gray-100"></div>
          <button
            onClick={() => handleDownload("pdf")}
            className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-red-50 w-full text-left transition-colors"
          >
            <FaFilePdf size={18} className="text-red-600" />
            <span className="font-medium">Download PDF</span>
          </button>
        </div>
      )}
    </div>
  );
};
